import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ArrowUpRight, UtensilsCrossed, Trees, HeartHandshake } from "lucide-react";
import { ZOMATO_URL, GOOGLE_URL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_RATINGS = {
    zomato: { rating: "4.3", count: "2,494 ratings" },
    google: { rating: "4.8", count: "1,346 reviews" },
};

const THEMES = [
    { icon: UtensilsCrossed, label: "Food that delights", testid: "review-theme-food" },
    { icon: Trees, label: "An ambience to unwind in", testid: "review-theme-ambience" },
    { icon: HeartHandshake, label: "Service with heart", testid: "review-theme-service" },
];

const QUOTES = [
    {
        text: "Esak at Uncover Avenue definitely lived up to the hype! This place has quickly become one of the most beautiful dining spots in Dwarka Sector 13. The architectural design is stunning, very modern, airy, and filled with natural light.",
        name: "Pratik Patel",
        meta: "Local Guide on Google",
        testid: "quote-pratik",
    },
    {
        text: "Ambiance is nice. So many photo points. Service is good. Visited here for a birthday party, nicely organised. Food is good.",
        name: "Leena Gadge",
        meta: "Local Guide on Google",
        testid: "quote-leena",
    },
    {
        text: "It's an amazing place to hang out with friends and family. Food is amazing here, I loved the Chinese food. My wife and kids enjoyed a lot. Aakash gave us amazing hospitality. Must recommend place to visit.",
        name: "BMH Accounts",
        meta: "Google review",
        testid: "quote-bmh",
    },
];

export const ReviewsStrip = () => {
    const [ratings, setRatings] = useState(DEFAULT_RATINGS);

    useEffect(() => {
        axios
            .get(`${API}/ratings`)
            .then((res) => {
                if (res.data?.zomato?.rating) setRatings(res.data);
            })
            .catch(() => {});
    }, []);

    const platforms = [
        { name: "Zomato", ...ratings.zomato, url: ZOMATO_URL, testid: "review-card-zomato" },
        { name: "Google", ...(ratings.google || DEFAULT_RATINGS.google), url: GOOGLE_URL, testid: "review-card-google" },
    ];

    return (
        <section id="reviews" className="py-20 md:py-28 px-6 md:px-10 bg-white" data-testid="reviews-section">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center max-w-2xl mx-auto"
                >
                    <p className="font-serif-brand italic text-xl text-[#F0841B] mb-3">Loved by our guests</p>
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#262055]" data-testid="reviews-headline">
                        Rated by the people<br />who matter most
                    </h2>
                </motion.div>

                <div className="mt-14 grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="reviews-cards">
                    {platforms.map((p, i) => (
                        <motion.a
                            key={p.name}
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="group rounded-[2rem] bg-[#FFF7C5] p-8 md:p-10 text-center shadow-lg shadow-[#262055]/5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                            data-testid={p.testid}
                        >
                            <div className="flex items-center justify-center gap-1.5" data-testid={`${p.testid}-stars`}>
                                {[...Array(5)].map((_, s) => (
                                    <Star key={s} size={22} className={s < Math.round(Number(p.rating)) ? "fill-[#F0841B] text-[#F0841B]" : "fill-[#F0841B]/40 text-[#F0841B]/40"} />
                                ))}
                            </div>
                            <p className="mt-4 text-5xl font-extrabold tracking-tighter text-[#262055]" data-testid={`${p.testid}-rating`}>{p.rating}</p>
                            <p className="mt-1 text-sm font-semibold text-[#262055]/60" data-testid={`${p.testid}-count`}>{p.count} on {p.name}</p>
                            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#00958C] group-hover:gap-2.5 transition-all duration-200">
                                Read reviews on {p.name} <ArrowUpRight size={15} />
                            </span>
                        </motion.a>
                    ))}
                </div>

                <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-testid="review-quotes-grid">
                    {QUOTES.map((q, i) => (
                        <motion.figure
                            key={q.name}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.12 }}
                            className="rounded-[2rem] border border-[#262055]/10 bg-[#FFF7C5]/40 p-8 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                            data-testid={q.testid}
                        >
                            <span className="font-serif-brand text-5xl leading-none text-[#F0841B]" aria-hidden="true">"</span>
                            <blockquote className="mt-3 text-sm md:text-base leading-relaxed text-[#262055]/80 flex-1" data-testid={`${q.testid}-text`}>
                                {q.text}
                            </blockquote>
                            <figcaption className="mt-6 pt-5 border-t border-[#262055]/10">
                                <p className="font-semibold text-[#262055]" data-testid={`${q.testid}-name`}>{q.name}</p>
                                <p className="text-xs text-[#262055]/50 mt-0.5">{q.meta}</p>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mt-10 flex flex-wrap justify-center gap-3"
                    data-testid="reviews-themes"
                >
                    {THEMES.map((t) => (
                        <span key={t.label} className="inline-flex items-center gap-2 rounded-full border border-[#262055]/12 bg-[#FFF7C5]/60 px-5 py-2.5 text-sm font-medium text-[#262055]" data-testid={t.testid}>
                            <t.icon size={16} className="text-[#00958C]" />
                            {t.label}
                        </span>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};
